// src/hooks/useProjects.js

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const useProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Состояния для модального окна создания проекта
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectColor, setNewProjectColor] = useState(''); // <-- ИЗМЕНЕНИЕ: убрали цвет по умолчанию отсюда

  // Состояние для меню и редактирования
  const [projectMenuOpen, setProjectMenuOpen] = useState(null);
  const [editingProject, setEditingProject] = useState(null);

  // Состояние для модального окна удаления
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/projects');
      setProjects(res.data);
      setError('');
    } catch (err) {
      console.error('Ошибка при загрузке проектов:', err);
      setError('Не удалось загрузить проекты');
      toast.error('Не удалось загрузить проекты');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Закрытие выпадающего меню при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectMenuOpen) {
        const isMenuButton = event.target.closest('.project-menu-btn');
        const isDropdown = event.target.closest('.project-menu-dropdown');
        if (!isMenuButton && !isDropdown) {
          setProjectMenuOpen(null);
        }
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [projectMenuOpen]);


  const createProject = async () => {
    if (!newProjectName.trim()) {
      toast.error('Введите название проекта');
      return;
    }
    // ИЗМЕНЕНИЕ: Проверяем, что цвет был установлен
    if (!newProjectColor) {
      toast.error('Выберите цвет проекта');
      return;
    }
    try {
      const res = await axios.post('/api/projects', {
        name: newProjectName.trim(),
        description: '',
        color: newProjectColor
      });
      setProjects(prev => [...prev, res.data]);
      setCreateModalOpen(false);
      setNewProjectName('');
      setNewProjectColor(''); // Сбрасываем цвет
      toast.success('Проект создан');
    } catch (err) {
      console.error('Ошибка при создании проекта:', err);
      toast.error('Не удалось создать проект');
    }
  };

  const renameProject = async (projectId, newName) => {
    if (!newName || !newName.trim()) {
      setEditingProject(null);
      return;
    }
    try {
      const res = await axios.put(`/api/projects/${projectId}`, {
        name: newName.trim()
      });
      setProjects(prev => prev.map(p => p._id === projectId ? res.data : p));
      toast.success('Проект переименован');
    } catch (err) {
      console.error('Ошибка при переименовании проекта:', err);
      toast.error('Не удалось переименовать проект');
    } finally {
        setEditingProject(null);
    }
  };

  const openDeleteModal = (projectId) => {
    setProjectToDelete(projectId);
    setDeleteModalOpen(true);
    setProjectMenuOpen(null);
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;
    try {
      await axios.delete(`/api/projects/${projectToDelete}`);
      const deletedProjectId = projectToDelete;
      setProjects(prev => prev.filter(p => p._id !== deletedProjectId));
      toast.success('Проект удален');
      return deletedProjectId; 
    } catch (err) {
      console.error('Ошибка при удалении проекта:', err);
      toast.error('Не удалось удалить проект');
      return null;
    } finally {
      setDeleteModalOpen(false);
      setProjectToDelete(null);
    }
  };

  const cancelDeleteProject = () => {
    setDeleteModalOpen(false);
    setProjectToDelete(null);
  };
  
  const openCreateModal = () => {
      setNewProjectName('');
      setNewProjectColor(''); // Сбрасываем, чтобы useEffect в модалке сработал
      setCreateModalOpen(true);
  }

  // ИЗМЕНЕНИЕ: Мы возвращаем объект `createModalProps` для удобства
  return {
    projects,
    loading,
    error,
    
    actions: {
        openCreateModal,
        renameProject,
        openDeleteModal,
        setEditingProject,
        setProjectMenuOpen,
    },

    // Эти пропсы мы передадим напрямую в <CreateProjectModal />
    createModalProps: {
      show: isCreateModalOpen,
      projectName: newProjectName,
      setProjectName: setNewProjectName,
      projectColor: newProjectColor,
      setProjectColor: setNewProjectColor,
      onClose: () => setCreateModalOpen(false),
      onCreateProject: createProject,
    },
    
    // Модальное окно удаления (остается как было)
    modals: {
        delete: {
            isOpen: isDeleteModalOpen,
            close: cancelDeleteProject,
            confirm: confirmDeleteProject,
        }
    },

    projectMenuOpen,
    editingProject,
  };
};

export default useProjects;